import { useState } from "react";
import { likelySignedIn, useAuth } from "@/auth/session";
import JobsBoard from "@/components/jobs/JobsBoard";
import JobsHeader from "@/components/jobs/JobsHeader";
import ExtensionBanner from "@/components/site/ExtensionBanner";
import { ClosingBand, OnePosting } from "@/components/site/VisitorBits";
import { useJobsSearch } from "@/lib/hooks/jobsSearch";
import { boardLine } from "@/lib/jobs";
import { useTitleSearch } from "@/lib/useTitleSearch";

/**
 * Jobs, the site's home, for everyone: every open posting in the catalogue, searched by the page's
 * words, filtered by how it is worked and how recently it went up, with where the person's application to each stands from envoy
 * and the fit of their resume from the resume service.
 * Arrived at with no words, it searches the latest title on their resume.
 *
 * Signed out (design/artboards/SiteJobs.dc.html): the headline over the
 * search, Get new matches by email, Save on every card (the sheet), no
 * statuses or fit, and under the board One posting, start to finish and
 * the gradient band. Signed in: the Jobs title, the
 * fit and where each application stands, and Save at once.
 */
export default function Jobs() {
    const { state } = useAuth();
    const member = state.status === "signed-in";
    const visitor = state.status === "signed-out";
    const standing = member ? "member" : visitor ? "visitor" : "unknown";
    // While the sign-in is read, the header and the toolbar take the shape
    // they will most likely land on, so the board does not move when it lands.
    const [guess] = useState<"member" | "visitor">(() => (likelySignedIn() ? "member" : "visitor"));
    const shape = standing === "unknown" ? guess : standing;
    useTitleSearch(standing);
    const search = useJobsSearch(member);

    return (
        <JobsBoard
            search={search}
            member={member}
            statusTabs={shape === "member"}
            showSave={state.status !== "loading"}
            visitor={shape === "visitor"}
            line={boardLine(search.source, search.view.status, search.board.total, search.view.q)}
            header={
                <>
                    <ExtensionBanner />
                    <JobsHeader standing={shape} />
                </>
            }
            footer={
                visitor && (
                    <>
                        <OnePosting />
                        <ClosingBand />
                    </>
                )
            }
        />
    );
}
